import React from 'react';
import styled from 'react-emotion';
import { cx } from 'react-emotion';
import { theme } from '../../Theme';
import Image from '@jetshop/ui/Image';
import useCleanProps from '../../../hooks/useCleanProps';
import { Above } from '@jetshop/ui/Breakpoints';
import MaxWidth from '../../Layout/MaxWidth';
import htmlContentStyle from '../htmlContentStyle';

const OuterContainer = styled('article')`
  max-width: 50rem;
  margin: 0 auto;

  &.image-placement-top,
  &.image-placement-bottom {
    .image-container {
      width: 100%;

      + .html {
        margin: 1.5rem 0 0;

        ${theme.below.md} {
          margin: 1rem 0 0;
        }
      }
    }

    .html + .image-container {
      margin: 1.5rem 0 0;

      ${theme.below.md} {
        margin: 1rem 0 0;
      }
    }
  }

  &.image-placement-left,
  &.image-placement-right {
    .image-container {
      width: 50% !important;
      margin-bottom: 1rem;
    }
  }

  &.image-placement-left {
    .image-container {
      float: left;
      padding-right: 1.25rem;
    }
  }

  &.image-placement-right {
    .image-container {
      float: right;
      padding-left: 1.25rem;
    }
  }

  &.two-columns {
    ${theme.above.md} {
      .html {
        column-count: 2;
        gap: 2rem;
      }
    }
  }

  &.full-width {
    max-width: none;
    width: 100%;

    .html {
      max-width: none;
    }
  }

  .html {
    width: 100%;

    > :first-child {
      margin-top: 0;
    }

    > :last-child {
      margin-bottom: 0;
    }
  }
`;

const ImageContainer = styled('div')`
  > div {
    overflow: hidden;
  }
`;

const HtmlContainer = ({ html }) => {
  return (
    <div
      className={cx(htmlContentStyle, 'html')}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

const HtmlImageBlock = props => {

  const {
    desktopImage,
    desktopImageRatio = '1:1',
    mobileImage,
    mobileImageRatio = '1:1',
    imageAltText,
    imagePlacement = 'Top',
    fullWidth,
    twoColumns = false,
    html,
    spaceAbove = '16px'
  } = useCleanProps(props);

  if (!props) return null;

  // CLEAN HTML FROM EMPTY P TAGS FROM EDITOR
  let cleanedHtml;
  if (html) {
    cleanedHtml = html.replace(/<p><br><\/p>/g, '');
  }

  // DO NOT ALLOW LEFT AND RIGHT IMAGE FOR TWO COLUMNS
  let adjustedImagePlacement = imagePlacement;
  if (twoColumns && (imagePlacement === 'Left' || imagePlacement === 'Right')) {
    adjustedImagePlacement = 'Top';
  }

  const sizes =
    !!adjustedImagePlacement.match(/Top|Bottom/i) ?
      fullWidth ? [1, 1, 1, 1248] :
        [1, 1, 1, 960] :
      fullWidth ? [1 / 2, 1 / 2, 1 / 2, 624] :
        [1 / 2, 1 / 2, 1 / 2, 460];

  return (
      <OuterContainer
        style={{ marginTop: spaceAbove }}
        className={cx(
          `image-placement-${adjustedImagePlacement.toLowerCase()}`,
          `${twoColumns ? 'two-columns' : ''}`,
          `${fullWidth ? 'full-width' : ''}`
        )}
      >
        {cleanedHtml && adjustedImagePlacement === 'Bottom' && (
          <HtmlContainer html={cleanedHtml} />
        )}
        {desktopImage && (
          <Above breakpoint={'md'}>
            {matches => (
              <ImageContainer className={'image-container'}>
                <Image
                  src={matches || !mobileImage ? desktopImage : mobileImage}
                  alt={imageAltText}
                  sizes={sizes}
                  crop={true}
                  critical={false}
                  fillAvailableSpace={false}
                  aspect={
                    matches || !mobileImage
                      ? desktopImageRatio
                      : mobileImageRatio
                  }
                />
              </ImageContainer>
            )}
          </Above>
        )}
        {cleanedHtml && adjustedImagePlacement !== 'Bottom' && (
          <HtmlContainer html={cleanedHtml} />
        )}
      </OuterContainer>
  );
};

export default HtmlImageBlock;
